import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getDepartments, createDepartment, updateDepartment, deleteDepartment } from '../../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiList } from 'react-icons/fi';
import Toast from '../../components/Toast';

const Departments = () => {
  const { t } = useTranslation();
  const [type, setType] = useState('department');
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (msg, t = 'success') => { setToast({ msg, type: t }); setTimeout(() => setToast(null), 3000); };

  const fetch = () => getDepartments({ type }).then(r => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { fetch(); }, [type]);

  const handleAdd = async () => {
    setError('');
    if (!name.trim()) return setError('Name is required');
    setSaving(true);
    try {
      await createDepartment({ name: name.trim(), type });
      setShowAdd(false);
      setName('');
      showToast(`${type === 'department' ? 'Department' : 'Zone'} created`);
      fetch();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleEdit = async () => {
    setError('');
    if (!editItem.name.trim()) return setError('Name is required');
    setSaving(true);
    try {
      await updateDepartment(editItem.id, { name: editItem.name, type: editItem.type });
      setEditItem(null);
      showToast('Updated');
      fetch();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    await deleteDepartment(item.id);
    showToast('Deleted', 'error');
    fetch();
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('nav.departments')}</h1></div>
        <button className="btn btn-primary" onClick={() => { setShowAdd(true); setName(''); setError(''); }}><FiPlus /> {t('common.add')}</button>
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e5e7eb', marginBottom: 20, width: 'fit-content' }}>
        {['department', 'zone'].map(tab => (
          <button key={tab} onClick={() => setType(tab)} style={{
            padding: '8px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: 13, color: type === tab ? '#4f46e5' : '#6b7280',
            borderBottom: type === tab ? '2px solid #4f46e5' : '2px solid transparent',
            textTransform: 'capitalize'
          }}>{tab === 'department' ? t('table.department') : t('table.zone')}</button>
        ))}
      </div>

      <div className="table-card">
        <table>
          <thead><tr><th>#</th><th>{t('common.name')}</th><th>{t('table.type')}</th><th>{t('common.actions')}</th></tr></thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={4}><div className="empty-state"><div className="empty-icon"><FiList /></div><p>{t('finance.noRecords')}</p></div></td></tr>
            ) : items.map((item, i) => (
              <tr key={item.id}>
                <td data-label="#">{i + 1}</td>
                <td data-label={t('common.name')}><strong>{item.name}</strong></td>
                <td data-label={t('table.type')}><span className={`pill ${item.type === 'department' ? 'pill-blue' : 'pill-purple'}`} style={{ textTransform: 'capitalize' }}>{item.type}</span></td>
                <td data-label={t('common.actions')}>
                  <div className="action-btns">
                    <button className="btn-icon" onClick={() => { setEditItem({ ...item }); setError(''); }}><FiEdit2 /></button>
                    <button className="btn-icon danger" onClick={() => handleDelete(item)}><FiTrash2 /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <div><h3>Add {type === 'department' ? 'Department' : 'Zone'}</h3></div>
              <button className="btn-icon" onClick={() => setShowAdd(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              {error && <div className="error-msg">{error}</div>}
              <div className="form-group"><label>Name <span className="required">*</span></label><input value={name} onChange={e => setName(e.target.value)} autoFocus /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>{saving ? 'Saving...' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {editItem && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <div><h3>Edit {editItem.type === 'department' ? 'Department' : 'Zone'}</h3></div>
              <button className="btn-icon" onClick={() => setEditItem(null)}><FiX /></button>
            </div>
            <div className="modal-body">
              {error && <div className="error-msg">{error}</div>}
              <div className="form-group"><label>Name <span className="required">*</span></label><input value={editItem.name} onChange={e => setEditItem(p => ({ ...p, name: e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditItem(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleEdit} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default Departments;
